import {
  encodeChecklistRosterSortKey,
  normalizeNumber,
  type ChecklistNaturalKey,
} from './checklist-key';
import { SERIAL_TO_INGEST_MAX } from './types';
/** Human corrections are an overlay, never a new provider snapshot fingerprint. */
export const CHECKLIST_CORRECTION_FIELDS = [
  'number',
  'name',
  'team',
  'rookie',
  'autograph',
  'memorabilia',
  'serialTo',
] as const;
export type ChecklistCorrectionField = (typeof CHECKLIST_CORRECTION_FIELDS)[number];
export type ChecklistCorrectionValues = Partial<
  Record<ChecklistCorrectionField, string | boolean | number | null>
>;
export interface ChecklistCorrectionSource {
  system: 'azure-sql-ebay';
  targetId: string;
  itemId: string;
  snapshotFingerprint: string;
}
export interface ChecklistRowCorrection {
  correctionId: string;
  operation: 'UPDATE_ROW';
  source: ChecklistCorrectionSource;
  before: ChecklistCorrectionValues;
  after: ChecklistCorrectionValues;
  reason: string;
  cardIds?: string[];
}
export interface ChecklistCorrectionReversal {
  correctionId: string;
  operation: 'REVERSE';
  reversesCorrectionId: string;
  reason: string;
}
export type ChecklistCorrectionChange = ChecklistRowCorrection | ChecklistCorrectionReversal;
export interface ChecklistCorrectionsFile {
  schemaVersion: 1;
  batchId: string;
  exportId: string;
  runId: string;
  producer: { tool: string; version?: string };
  changes: ChecklistCorrectionChange[];
}
export interface ChecklistCorrectionPreviewRow {
  correctionId: string;
  status: 'READY' | 'CONFLICT' | 'ALREADY_APPLIED';
  source?: ChecklistCorrectionSource;
  current?: ChecklistCorrectionValues;
  proposed?: ChecklistCorrectionValues;
  code?: string;
  message?: string;
  resolution?: string;
}
export interface ChecklistCorrectionPreview {
  rows: ChecklistCorrectionPreviewRow[];
  previewToken: string;
}
export interface ChecklistCorrectionApplyRow extends Omit<ChecklistCorrectionPreviewRow, 'status'> {
  status: 'APPLIED' | 'CONFLICT' | 'ALREADY_APPLIED';
}
const object = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown, max = 128): v is string =>
  typeof v === 'string' && v.trim().length > 0 && v.length <= max;
function keys(v: Record<string, unknown>, allowed: string[]): boolean {
  return Object.keys(v).every((k) => allowed.includes(k));
}
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
function values(v: unknown, before = false): v is ChecklistCorrectionValues {
  if (!object(v) || !keys(v, [...CHECKLIST_CORRECTION_FIELDS]) || Object.keys(v).length === 0)
    return false;
  return Object.entries(v).every(([k, x]) => {
    if (before && x === null) return true;
    if (['rookie', 'autograph', 'memorabilia'].includes(k)) return typeof x === 'boolean';
    if (k === 'serialTo')
      return (
        x === null ||
        (typeof x === 'number' && Number.isSafeInteger(x) && x > 0 && x <= SERIAL_TO_INGEST_MAX)
      );
    if (k === 'team') return x === null || text(x, 128);
    return text(x, k === 'name' ? 256 : 64);
  });
}
export function parseChecklistCorrectionsFile(
  input: unknown,
): { ok: true; value: ChecklistCorrectionsFile } | { ok: false; error: string } {
  const invalid = (error: string) => ({ ok: false as const, error });
  if (
    !object(input) ||
    !keys(input, ['schemaVersion', 'batchId', 'exportId', 'runId', 'producer', 'changes']) ||
    input.schemaVersion !== 1 ||
    !text(input.batchId) ||
    !/^tcvs:batch:[0-7][0-9A-HJKMNP-TV-Z]{25}$/.test(input.batchId) ||
    !text(input.exportId) ||
    !uuid.test(input.exportId) ||
    !text(input.runId) ||
    !object(input.producer) ||
    !keys(input.producer, ['tool', 'version']) ||
    !text(input.producer.tool) ||
    (input.producer.version !== undefined && !text(input.producer.version)) ||
    !Array.isArray(input.changes) ||
    input.changes.length === 0 ||
    input.changes.length > 200
  )
    return invalid('INVALID_CORRECTION_FILE');
  const ids = new Set<string>();
  const rows = new Set<string>();
  for (const change of input.changes) {
    if (
      !object(change) ||
      !text(change.correctionId) ||
      !/^[a-zA-Z0-9:_-]+$/.test(change.correctionId) ||
      ids.has(change.correctionId) ||
      !text(change.reason, 1000)
    )
      return invalid('INVALID_OR_DUPLICATE_CORRECTION');
    ids.add(change.correctionId);
    if (change.operation === 'REVERSE') {
      if (
        !keys(change, ['correctionId', 'operation', 'reversesCorrectionId', 'reason']) ||
        !text(change.reversesCorrectionId) ||
        !/^[a-zA-Z0-9:_-]+$/.test(change.reversesCorrectionId) ||
        change.reversesCorrectionId === change.correctionId
      )
        return invalid('INVALID_REVERSAL');
    } else if (change.operation === 'UPDATE_ROW') {
      const s = change.source;
      if (
        !keys(change, [
          'correctionId',
          'operation',
          'source',
          'before',
          'after',
          'reason',
          'cardIds',
        ]) ||
        !object(s) ||
        !keys(s, ['system', 'targetId', 'itemId', 'snapshotFingerprint']) ||
        s.system !== 'azure-sql-ebay' ||
        !text(s.targetId) ||
        !/^\d+$/.test(s.targetId) ||
        !text(s.itemId) ||
        !/^\d+$/.test(s.itemId) ||
        !text(s.snapshotFingerprint) ||
        !/^[a-f0-9]{16,64}$/i.test(s.snapshotFingerprint) ||
        !values(change.before, true) ||
        !values(change.after) ||
        Object.keys(change.before).sort().join() !== Object.keys(change.after).sort().join() ||
        (change.cardIds !== undefined &&
          (!Array.isArray(change.cardIds) ||
            change.cardIds.length > 200 ||
            !change.cardIds.every((id) => typeof id === 'string' && uuid.test(id)) ||
            new Set(change.cardIds).size !== change.cardIds.length))
      )
        return invalid('INVALID_ROW_CORRECTION');
      const key = `${s.system}#${s.targetId}#${s.itemId}`;
      if (rows.has(key)) return invalid('DUPLICATE_SOURCE_ROW');
      rows.add(key);
      const before = change.before as ChecklistCorrectionValues;
      const after = change.after as ChecklistCorrectionValues;
      if (
        Object.keys(before).every(
          (k) => before[k as ChecklistCorrectionField] === after[k as ChecklistCorrectionField],
        )
      )
        return invalid('NO_CHANGE');
    } else return invalid('UNSUPPORTED_CORRECTION_OPERATION');
  }
  return { ok: true, value: input as unknown as ChecklistCorrectionsFile };
}
export function checklistCorrectionValuesMatch(
  current: ChecklistCorrectionValues,
  expected: ChecklistCorrectionValues,
): boolean {
  return Object.keys(expected).every(
    (k) =>
      (current[k as ChecklistCorrectionField] ?? null) === expected[k as ChecklistCorrectionField],
  );
}
export function readChecklistCorrectionValues(
  reference: Record<string, unknown>,
  source: Record<string, unknown>,
): ChecklistCorrectionValues {
  const identity = reference.identity as Record<string, unknown>;
  return {
    number: String(identity.number),
    name: String(identity.name),
    team: typeof identity.team === 'string' ? identity.team : null,
    rookie: typeof source.rookie === 'boolean' ? source.rookie : null,
    autograph: typeof source.autograph === 'boolean' ? source.autograph : null,
    memorabilia: typeof source.memorabilia === 'boolean' ? source.memorabilia : null,
    serialTo: typeof source.serialTo === 'number' ? source.serialTo : null,
  };
}
/** Preserve provider provenance; remove obsolete title-safe values when display text changes. */
export function projectChecklistCorrection(
  reference: Record<string, unknown>,
  source: Record<string, unknown>,
  after: ChecklistCorrectionValues,
  revision: string,
): { reference: Record<string, unknown>; source: Record<string, unknown> } {
  const r = structuredClone(reference);
  const s = structuredClone(source);
  const providerValues = readChecklistCorrectionValues(reference, source);
  const identity = r.identity as Record<string, unknown>;
  const naturalKey = r.naturalKey as ChecklistNaturalKey;
  for (const [field, value] of Object.entries(after)) {
    if (value === null) {
      delete identity[field];
      delete s[field];
    } else {
      identity[field] = value;
      s[field] = value;
    }
    if (field === 'serialTo') identity.serialNumbered = value !== null;
    if (s.marketplaceAspects) {
      const aspects = s.marketplaceAspects as Record<string, unknown>;
      for (const aspect of Object.keys(aspects)) {
        if (
          (field === 'name' && /player|athlete|character|card[\s_-]*name|^name$/i.test(aspect)) ||
          (field === 'number' && /^(card[\s_-]*)?(number|#)$/i.test(aspect)) ||
          (field === 'team' && /team/i.test(aspect)) ||
          (['rookie', 'autograph', 'memorabilia', 'serialTo'].includes(field) &&
            /feature|rookie|autograph|memorabilia|serial|print[\s_-]*run|numbered/i.test(aspect))
        )
          delete aspects[aspect];
      }
    }
    if (field === 'name') {
      delete s.nameTitleSafe;
      delete s.players;
      delete s.featuredPerson;
      if (r.titleSafe) delete (r.titleSafe as Record<string, unknown>).name;
    }
    if (field === 'number') naturalKey.number = normalizeNumber(String(value)) || '_';
  }
  r.sk = encodeChecklistRosterSortKey(naturalKey);
  s.checklistSk = r.sk;
  r.correctionRevision = revision;
  s.correctionRevision = revision;
  const fields = Object.keys(after);
  r.correctedFields = fields;
  s.correctedFields = fields;
  if (source.correctionRevision !== revision)
    s.correctionProviderValues = Object.fromEntries(
      fields.map((field) => [field, providerValues[field as ChecklistCorrectionField]]),
    );
  r.sourceOwnership = {
    ...(r.sourceOwnership as Record<string, unknown>),
    correctionRevision: revision,
    correctedFields: fields,
  };
  return { reference: r, source: s };
}
export function buildChecklistCorrectionsSchema(): Record<string, unknown> {
  const string = (maxLength: number) => ({
    type: 'string',
    minLength: 1,
    maxLength,
    pattern: '\\S',
  });
  const id = { ...string(128), pattern: '^[a-zA-Z0-9:_-]+$' };
  const source = {
    type: 'object',
    additionalProperties: false,
    required: ['system', 'targetId', 'itemId', 'snapshotFingerprint'],
    properties: {
      system: { const: 'azure-sql-ebay' },
      targetId: { ...string(128), pattern: '^\\d+$' },
      itemId: { ...string(128), pattern: '^\\d+$' },
      snapshotFingerprint: { ...string(64), pattern: '^[a-fA-F0-9]{16,64}$' },
    },
  };
  const fieldProperties = {
    number: string(64),
    name: string(256),
    team: { anyOf: [string(128), { type: 'null' }] },
    rookie: { type: 'boolean' },
    autograph: { type: 'boolean' },
    memorabilia: { type: 'boolean' },
    serialTo: {
      anyOf: [{ type: 'integer', minimum: 1, maximum: SERIAL_TO_INGEST_MAX }, { type: 'null' }],
    },
  };
  const after = {
    type: 'object',
    additionalProperties: false,
    minProperties: 1,
    properties: fieldProperties,
  };
  const before = {
    ...after,
    properties: Object.fromEntries(
      Object.entries(fieldProperties).map(([k, v]) => [k, { anyOf: [v, { type: 'null' }] }]),
    ),
  };
  return {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    title: 'TCVS checklist corrections v1',
    type: 'object',
    additionalProperties: false,
    required: ['schemaVersion', 'batchId', 'exportId', 'runId', 'producer', 'changes'],
    properties: {
      schemaVersion: { const: 1 },
      batchId: { type: 'string', pattern: '^tcvs:batch:[0-7][0-9A-HJKMNP-TV-Z]{25}$' },
      exportId: {
        type: 'string',
        pattern: uuid.source.replaceAll('a-f', 'a-fA-F').replace('[89ab]', '[89aAbB]'),
      },
      runId: string(128),
      producer: {
        type: 'object',
        additionalProperties: false,
        required: ['tool'],
        properties: { tool: string(128), version: string(128) },
      },
      changes: {
        type: 'array',
        minItems: 1,
        maxItems: 200,
        items: {
          oneOf: [
            {
              type: 'object',
              additionalProperties: false,
              required: ['correctionId', 'operation', 'source', 'before', 'after', 'reason'],
              properties: {
                correctionId: id,
                operation: { const: 'UPDATE_ROW' },
                source,
                before,
                after,
                reason: string(1000),
                cardIds: {
                  type: 'array',
                  maxItems: 200,
                  uniqueItems: true,
                  items: {
                    type: 'string',
                    pattern: uuid.source.replaceAll('a-f', 'a-fA-F').replace('[89ab]', '[89aAbB]'),
                  },
                },
              },
            },
            {
              type: 'object',
              additionalProperties: false,
              required: ['correctionId', 'operation', 'reversesCorrectionId', 'reason'],
              properties: {
                correctionId: id,
                operation: { const: 'REVERSE' },
                reversesCorrectionId: id,
                reason: string(1000),
              },
            },
          ],
        },
      },
    },
  };
}
/** Stable registry commitment shared by native import validation and source-binding cutover. */
export function canonicalChecklistCorrectionRegistry(
  records: readonly Record<string, unknown>[],
): string {
  const canonical = (value: unknown): unknown =>
    Array.isArray(value)
      ? value.map(canonical)
      : value !== null && typeof value === 'object'
        ? Object.fromEntries(
            Object.entries(value)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([key, item]) => [key, canonical(item)]),
          )
        : value;
  return JSON.stringify(
    canonical([...records].sort((a, b) => String(a.sk).localeCompare(String(b.sk)))),
  );
}

export const CHECKLIST_CORRECTION_SOURCE_FIELDS = [
  'pk',
  'sk',
  'recordType',
  'schemaVersion',
  'sourceSystem',
  'sourceTargetId',
  'sourceItemId',
  'sourceSubsetId',
  'sourceFile',
  'marketplaceAspects',
  'rarity',
  'finish',
  'autographAuthentication',
  'autographAuthenticationNumber',
  'autographFormat',
  'illustrator',
  'featuredPerson',
  'eventTournament',
  'ccgAttributes',
  'ccgAttributesRaw',
  'subsetTitleSafe',
  'nameTitleSafe',
  'parallelName',
  'parallelNameTitleSafe',
  'serialTo',
  'cardType',
  'players',
  'notes',
  'rookie',
  'shortPrint',
  'shortPrintNote',
  'autograph',
  'memorabilia',
  'sourceFamilyId',
  'snapshotFingerprint',
  'checklistPk',
  'checklistSk',
  'subset',
  'number',
  'name',
  'team',
  'updatedAt',
  'correctionRevision',
  'correctedFields',
  'correctionProviderValues',
];
export const CHECKLIST_CORRECTION_REFERENCE_FIELDS = [
  'pk',
  'sk',
  'identity',
  'naturalKey',
  'sourceOwnership',
  'titleSafe',
  'unresolvedClassification',
  'correctionRevision',
  'correctedFields',
];
/** Compare complete projection fields, including absence, before replacing any materialized record. */
export function checklistCorrectionRecordFence(
  item: Record<string, unknown>,
  fields: string[],
): {
  ConditionExpression: string;
  ExpressionAttributeNames: Record<string, string>;
  ExpressionAttributeValues: Record<string, unknown>;
} {
  const names: Record<string, string> = {};
  const values: Record<string, unknown> = {};
  const clauses: string[] = [];
  for (const [index, field] of [...new Set([...fields, ...Object.keys(item)])].entries()) {
    const name = `#f${index}`;
    names[name] = field;
    if (item[field] === undefined) {
      clauses.push(`attribute_not_exists(${name})`);
    } else {
      const value = `:v${index}`;
      values[value] = item[field];
      clauses.push(`${name} = ${value}`);
    }
  }
  return {
    ConditionExpression: clauses.join(' AND '),
    ExpressionAttributeNames: names,
    ExpressionAttributeValues: values,
  };
}
